import React, { useEffect, useRef } from "react";

import { getExternalProducts } from "services/menu/rest";
import { ExternalProductDto } from "services/menu/restModels";

import { useConstants } from "shared/hooks/useConstants";
import { useCurrencyId } from "shared/hooks/useCurrencyId";
import { useLocale } from "shared/hooks/useLocale";
import { createCurrencyFormatter } from "shared/lib/currencyFormatter";
import { localizedText } from "shared/lib/localizedMap";
import { LabeledField, SearchableSelect } from "shared/ui";

import { useExternalProducts } from "features/MenuItemEditor/hooks/useExternalProducts";

import { OptionRow, OptionPrice, OptionTitle } from "./styles";

interface ExternalProductSelectProps {
    menuId: number;
    value: ExternalProductDto | null;
    onChange: (product: ExternalProductDto | null) => void;
    label?: string;
    hint?: string;
    // Позиция уже привязана, а сам товар с ней не приходит — подтягиваем его по id.
    initialExternalId?: string | null;
    disabled?: boolean;
}

export const ExternalProductSelect = ({
    menuId,
    value,
    onChange,
    label,
    hint,
    initialExternalId,
    disabled,
}: ExternalProductSelectProps) => {
    const locale = useLocale();
    const currencyId = useCurrencyId();
    const { constants } = useConstants();
    const catalog = useExternalProducts(menuId);
    const resolvedRef = useRef<string | null>(null);

    const currency = constants?.currencies.find((item) => item.id === currencyId) || null;
    const currencyFormatter = createCurrencyFormatter(currency, locale);

    useEffect(() => {
        if (!initialExternalId || value || resolvedRef.current === initialExternalId) return;

        resolvedRef.current = initialExternalId;

        getExternalProducts({ menuId, externalId: initialExternalId, skip: 0, limit: 1 })
            .then((page) => {
                const product = page?.items[0];

                if (product) onChange(product);
            })
            .catch(() => undefined);
    }, [menuId, initialExternalId, value, onChange]);

    const productLabel = (product: ExternalProductDto) =>
        `${localizedText(product.name, locale)} — ${currencyFormatter.toShow(product.price.price)}`;

    return (
        <LabeledField label={label} hint={hint}>
            <SearchableSelect<ExternalProductDto>
                value={value}
                onChange={onChange}
                options={catalog.products}
                getOptionKey={(product) => product.id}
                getOptionLabel={productLabel}
                renderOption={(product) => (
                    <OptionRow>
                        <OptionTitle>{localizedText(product.name, locale)}</OptionTitle>
                        <OptionPrice>{currencyFormatter.toShow(product.price.price)}</OptionPrice>
                    </OptionRow>
                )}
                query={catalog.query}
                onQueryChange={catalog.setQuery}
                onOpen={catalog.ensureLoaded}
                loading={catalog.loading}
                hasMore={catalog.hasMore}
                onLoadMore={catalog.loadMore}
                placeholder="Не привязана"
                searchPlaceholder="Поиск по названию"
                emptyText="Товары не найдены — проверьте синхронизацию номенклатуры"
                clearLabel="Не привязана"
                title="Товар в iiko"
                disabled={disabled}
            />
        </LabeledField>
    );
};
